import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faChevronDown, faChevronRight, faExpandAlt, faCompress } from '@fortawesome/free-solid-svg-icons';
import { REPORT_CARDS, ReportCard } from '../config/reportsConfig';
import { useAuthContext } from '../contexts/AuthContext';
import LoadingSpinner from '../shared/LoadingSpinner';
import './HomePage.css';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { allowedActions, loading, error } = useAuthContext();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => {
    try {
      const stored = sessionStorage.getItem('homePage.expandedCategories');
      return stored ? new Set<string>(JSON.parse(stored)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  // Filter by user permissions first, then by search query
  const filteredReports = useMemo(() => {
    const permitted = REPORT_CARDS.filter(
      (card) =>
        !card.permissionKey ||
        allowedActions.includes(`${card.permissionKey}:read`)
    );

    if (!searchQuery.trim()) {
      return permitted;
    }

    const query = searchQuery.toLowerCase();
    return permitted.filter(
      (card) =>
        card.endpoint.toLowerCase().includes(query) ||
        card.title.toLowerCase().includes(query) ||
        card.description.toLowerCase().includes(query) ||
        card.category.toLowerCase().includes(query)
    );
  }, [searchQuery, allowedActions]);

  // Group reports by category
  const groupedReports = useMemo(() => {
    const groups: { [key: string]: ReportCard[] } = {};
    filteredReports.forEach((card) => {
      if (!groups[card.category]) {
        groups[card.category] = [];
      }
      groups[card.category].push(card);
    });
    
    // Sort categories by number of reports (descending)
    return Object.keys(groups).sort((a, b) => groups[b].length - groups[a].length).reduce((acc, key) => {
      acc[key] = groups[key];
      return acc;
    }, {} as { [key: string]: ReportCard[] });
  }, [filteredReports]);

  // Check if all categories are expanded
  const areAllExpanded = useMemo(() => {
    const totalCategories = Object.keys(groupedReports).length;
    return totalCategories > 0 && expandedCategories.size === totalCategories;
  }, [expandedCategories, groupedReports]);

  const handleCardClick = (card: ReportCard) => {
    navigate(`/explorer?report=${encodeURIComponent(card.endpoint)}`);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const persistExpandedCategories = (categories: Set<string>) => {
    setExpandedCategories(categories);
    sessionStorage.setItem('homePage.expandedCategories', JSON.stringify(Array.from(categories)));
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    persistExpandedCategories(newExpanded);
  };

  const handleExpandAll = () => {
    const allCategories = new Set(Object.keys(groupedReports));
    persistExpandedCategories(allCategories);
  };

  const handleCollapseAll = () => {
    persistExpandedCategories(new Set());
  };

  const handleToggleAll = () => {
    if (areAllExpanded) {
      handleCollapseAll();
    } else {
      handleExpandAll();
    }
  };

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '2rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '520px' }}>
          <h2 style={{ color: '#d32f2f', marginBottom: '1rem' }}>Authentication Error</h2>
          <p style={{ color: '#555', lineHeight: '1.6' }}>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-page">
      <div className="home-page-header">
        <div className="header-left">
          <h1>Analytics Reports</h1>
          <p>Centralized reports for cross-service analytics and insights.</p>
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <div className="search-input-wrapper">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search reports by name or category..."
              className="search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search reports"
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={handleClearSearch}
                aria-label="Clear search"
                title="Clear search"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          {Object.values(groupedReports).flat().length > 0 && searchQuery && (
            <div className="search-results-count">
              Found {Object.values(groupedReports).flat().length} report{Object.values(groupedReports).flat().length !== 1 ? 's' : ''}
            </div>
          )}
        </div>

      </div>

      {Object.values(groupedReports).flat().length === 0 && searchQuery && (
        <div className="no-results">
          <p>No reports found matching "{searchQuery}"</p>
          <button 
            className="clear-button"
            onClick={handleClearSearch}
          >
            Clear Search
          </button>
        </div>
      )}

      {/* Expand/Collapse All Button */}
      {Object.keys(groupedReports).length > 0 && (
        <div className="categories-controls">
          <button
            className="expand-collapse-toggle-btn"
            onClick={handleToggleAll}
            aria-label={areAllExpanded ? "Collapse all categories" : "Expand all categories"}
            title={areAllExpanded ? "Collapse all categories" : "Expand all categories"}
          >
            <FontAwesomeIcon icon={areAllExpanded ? faCompress : faExpandAlt} />
            <span>{areAllExpanded ? 'Collapse all' : 'Expand all'}</span>
          </button>
        </div>
      )}

      <div className="reports-container">
        {Object.entries(groupedReports).map(([category, reports]) => (
          <div key={category} className="category-section">
            <div 
              className="category-header"
              onClick={() => toggleCategory(category)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleCategory(category);
                }
              }}
            >
              <FontAwesomeIcon 
                icon={expandedCategories.has(category) ? faChevronDown : faChevronRight} 
                className="category-icon"
              />
              <h2 className="category-title">{category}</h2>
              <span className="category-count">{reports.length}</span>
            </div>
            
            {expandedCategories.has(category) && (
              <div className="reports-grid">
                {reports.map((card) => (
                  <div
                    key={card.id}
                    className="report-card"
                    onClick={() => handleCardClick(card)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardClick(card);
                      }
                    }}
                  >
                    <div className="card-icon">
                      <FontAwesomeIcon icon={card.icon} />
                    </div>
                    <div className="card-content">
                      <h3 className="card-title">{card.title}</h3>
                      <p className="card-description">{card.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default HomePage;
